import React from 'react';
import { SimulationConfig } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';

interface OptimizationSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const OptimizationSettingsSection: React.FC<OptimizationSettingsSectionProps> = ({ config, setConfig }) => {
  return (
    <>
      <SectionLabel icon="fa-bolt" darkMode={config.darkMode}>Performance & Optimization</SectionLabel>
      <div className="flex flex-col gap-3 w-full p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-2">
        
        <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
          <span className="text-[10px] font-black uppercase opacity-60">Frustum Culling</span>
          <Toggle active={config.enableFrustumCulling} onClick={() => setConfig(p => ({...p, enableFrustumCulling: !p.enableFrustumCulling}))} color="bg-emerald-500" />
        </div>
        
        {config.enableFrustumCulling && (
          <SmartInput label="Culling Margin" min={0} max={1000} value={config.frustumCullingMargin} onChange={v => setConfig(p => ({...p, frustumCullingMargin: v}))} darkMode={config.darkMode} />
        )}

        <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
          <span className="text-[10px] font-black uppercase opacity-60">Cull Rings (Off-screen)</span>
          <Toggle active={config.cullRingsOutsideScreen} onClick={() => setConfig(p => ({...p, cullRingsOutsideScreen: !p.cullRingsOutsideScreen}))} color="bg-emerald-500" />
        </div>

        <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
          <span className="text-[10px] font-black uppercase opacity-60">Cull Blasts (Off-screen)</span>
          <Toggle active={config.cullBlastsOutsideScreen} onClick={() => setConfig(p => ({...p, cullBlastsOutsideScreen: !p.cullBlastsOutsideScreen}))} color="bg-emerald-500" />
        </div>

        <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
          <span className="text-[10px] font-black uppercase opacity-60">Partial Ring Blast Opt.</span>
          <Toggle active={config.partialRingBlastOptimization} onClick={() => setConfig(p => ({...p, partialRingBlastOptimization: !p.partialRingBlastOptimization}))} color="bg-emerald-500" />
        </div>

        <div className="h-px bg-white/5 my-1" />

        <SmartInput label="Max Active Blasts" min={10} max={1000} value={config.maxActiveBlasts} onChange={v => setConfig(p => ({...p, maxActiveBlasts: v}))} darkMode={config.darkMode} />
        <SmartInput label="Max Dust Particles" min={0} max={500} value={config.maxDustParticles} onChange={v => setConfig(p => ({...p, maxDustParticles: v}))} darkMode={config.darkMode} />
        <SmartInput label="Dust Resolution" min={0.1} max={1.0} step={0.1} value={config.dustResolution} onChange={v => setConfig(p => ({...p, dustResolution: v}))} darkMode={config.darkMode} />
        
        <div className="h-px bg-white/5 my-1" />
        
        <SmartInput label="Physics Substeps" min={1} max={16} value={config.physicsSubsteps} onChange={v => setConfig(p => ({...p, physicsSubsteps: v}))} darkMode={config.darkMode} />
        <SmartInput label="Max Velocity" min={100} max={20000} value={config.maxVelocityLimit} onChange={v => setConfig(p => ({...p, maxVelocityLimit: v}))} darkMode={config.darkMode} />
        
        <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
          <span className="text-[10px] font-black uppercase opacity-60">Hyper Speed Mode</span>
          <Toggle active={config.enableHyperSpeed} onClick={() => setConfig(p => ({...p, enableHyperSpeed: !p.enableHyperSpeed}))} color="bg-amber-500" />
        </div>

        {config.enableHyperSpeed && (
          <>
            <SmartInput label="Hyper Batch Size" min={1} max={200} value={config.hyperSpeedBatchSize} onChange={v => setConfig(p => ({...p, hyperSpeedBatchSize: v}))} darkMode={config.darkMode} />
            <SmartInput label="Hyper Render Limit" min={10} max={500} value={config.hyperSpeedRenderLimit} onChange={v => setConfig(p => ({...p, hyperSpeedRenderLimit: v}))} darkMode={config.darkMode} />
            <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
              <span className="text-[10px] font-black uppercase opacity-60">Simplified Visuals</span>
              <Toggle active={config.hyperSpeedSimplifiedVisuals} onClick={() => setConfig(p => ({...p, hyperSpeedSimplifiedVisuals: !p.hyperSpeedSimplifiedVisuals}))} />
            </div>
          </>
        )}
      </div>
    </>
  );
};
